import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

function OrderDetailModal({ order, theme, onClose, onOrderReopened, onCreateInvoice, onOrderDeleted, user }) {
    const { t } = useLanguage();
    useModalClose(onClose);
    const [orderItems, setOrderItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reopening, setReopening] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [allOptions, setAllOptions] = useState([]);

    // Check if user can delete orders (testdev user or owner role)
    const canDeleteOrders = user?.user_name === 'testdev' || user?.role_title?.toLowerCase() === 'owner';

    useEffect(() => {
        if (order) {
            fetchOrderItems();
            fetchOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    const fetchOptions = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/debug/options`);
            if (response.ok) {
                const data = await response.json();
                setAllOptions(data);
            }
        } catch (err) {
            console.error('Error fetching options:', err);
        }
    };

    const fetchOrderItems = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/order-products?order_id=${order.id}`);
            if (response.ok) {
                const data = await response.json();
                setOrderItems(data);
            }
        } catch (err) {
            console.error('Error fetching order items:', err);
        } finally {
            setLoading(false);
        }
    };

    const getOptionNames = (item) => {
        if (!item.options || !allOptions.length) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;

        return optionIds
            .map(id => allOptions.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    };

    const handleReopenOrder = async () => {
        if (!order || order.closed !== 1) return;

        const confirmed = window.confirm(t('confirmReopenOrder'));
        if (!confirmed) return;

        setReopening(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: order.name,
                    table_id: order.table_id,
                    closed: 0,
                    tip: 0
                    // Don't send payment_type_id, heard_about_id, or close_user_id
                    // Let them keep their default values when reopening
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to reopen order');
            }

            alert(t('orderReopenedSuccessfully'));
            onOrderReopened();
            onClose();
        } catch (err) {
            console.error('Error reopening order:', err);
            alert(t('error') + ': ' + err.message);
        } finally {
            setReopening(false);
        }
    };

    const handleDeleteOrder = async () => {
        if (!order) return;

        if (!window.confirm(t('deleteOrderConfirmation').replace('this order', `"${order.name}"`))) {
            return;
        }

        setDeleting(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/orders/${order.id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete order');
            }

            alert(t('orderDeleted') + ' ' + t('success').toLowerCase());
            if (onOrderDeleted) onOrderDeleted(order.id);
            onClose();
        } catch (err) {
            console.error('Error deleting order:', err);
            alert(t('error') + ': ' + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const calculateTotal = () => {
        return orderItems.reduce((sum, item) => {
            const itemPrice = Number(item.price || 0) * item.quantity;
            const discount = Number(item.discount || 0) * item.quantity;
            return sum + itemPrice - discount;
        }, 0);
    };

    if (!order) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: theme?.colors?.surface || '#fff',
                    borderRadius: theme?.borderRadius || 8,
                    padding: '2rem',
                    maxWidth: '800px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    borderBottom: `1px solid ${theme?.colors?.primary}20`,
                    paddingBottom: '1rem'
                }}>
                    <h2 style={{
                        color: theme?.colors?.primary || '#1976d2',
                        margin: 0
                    }}>
                        {t('orderDetails')} #{order.id}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: theme?.colors?.textSecondary
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* Order Info */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 6,
                    border: '1px solid #ddd'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '0.5rem',
                        fontSize: '0.9rem'
                    }}>
                        <div><strong>{t('name')}:</strong> {order.name}</div>
                        <div><strong>{t('table')}:</strong> {order.table_name || 'N/A'}</div>
                        <div><strong>{t('status')}:</strong>
                            <span style={{
                                color: order.closed ? '#4caf50' : '#ff9800',
                                fontWeight: 'bold',
                                marginLeft: '0.25rem'
                            }}>
                                {order.closed ? t('closed') : t('open')}
                            </span>
                        </div>
                        <div><strong>{t('created')}:</strong> {new Date(order.created).toLocaleString()}</div>
                        {order.closed && order.closed_at && (
                            <div><strong>{t('closed')}:</strong> {new Date(order.closed_at).toLocaleString()}</div>
                        )}
                        <div><strong>{t('openedBy')}:</strong> {order.open_user_name || t('unknown')}</div>
                        {order.closed && (
                            <div><strong>{t('closedBy')}:</strong> {order.close_user_name || t('unknown')}</div>
                        )}
                        {order.closed && order.payment_type_name && (
                            <div><strong>{t('paymentMethod')}:</strong> {order.payment_type_name}</div>
                        )}
                        {order.tip && Number(order.tip) > 0 && (
                            <div><strong>{t('tip')}:</strong> ${Number(order.tip).toFixed(2)}</div>
                        )}
                    </div>
                </div>

                {/* Order Items */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{
                        color: theme?.colors?.primary,
                        marginBottom: '1rem'
                    }}>
                        {t('orderItems')} ({orderItems.length})
                    </h3>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            {t('loading')}...
                        </div>
                    ) : orderItems.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: theme?.colors?.textSecondary
                        }}>
                            {t('noItemsInOrder')}
                        </div>
                    ) : (
                        <div style={{
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #ddd',
                            borderRadius: theme?.borderRadius || 4
                        }}>
                            {orderItems.map(item => {
                                const itemPrice = Number(item.price || 0) * item.quantity;
                                const discount = Number(item.discount || 0);
                                const finalPrice = itemPrice - discount;
                                const optionNames = getOptionNames(item);

                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            padding: '1rem',
                                            borderBottom: '1px solid #eee',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <div style={{
                                                fontWeight: 'bold',
                                                color: theme?.colors?.text,
                                                marginBottom: '0.25rem'
                                            }}>
                                                {item.product_name} x{item.quantity}
                                            </div>
                                            {optionNames && optionNames.length > 0 && (
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: theme?.colors?.textSecondary,
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    {optionNames.join(', ')}
                                                </div>
                                            )}
                                            {item.note && (
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    color: theme?.colors?.textSecondary,
                                                    fontStyle: 'italic'
                                                }}>
                                                    {t('note')}: {item.note}
                                                </div>
                                            )}
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: theme?.colors?.textSecondary
                                            }}>
                                                {t('status')}: {item.status === 0 ? t('pending') : item.status === 1 ? t('inProgress') : t('finished')}
                                            </div>
                                        </div>
                                        <div style={{
                                            fontWeight: 'bold',
                                            color: theme?.colors?.text,
                                            marginLeft: '1rem'
                                        }}>
                                            ${finalPrice.toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Total */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.primary || '#1976d2',
                    color: '#fff',
                    borderRadius: theme?.borderRadius || 6,
                    textAlign: 'center'
                }}>
                    <h3 style={{ margin: '0', fontSize: '1.2rem' }}>
                        {t('total')}: ${calculateTotal().toFixed(2)}
                    </h3>
                    {order.tip && Number(order.tip) > 0 && (
                        <div style={{ fontSize: '1rem', marginTop: '0.5rem' }}>
                            {t('withTip')}: ${(calculateTotal() + Number(order.tip)).toFixed(2)}
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('close')}
                    </button>

                    {order.closed === 1 && onCreateInvoice && (
                        <button
                            onClick={() => {
                                onCreateInvoice(order.id);
                                onClose();
                            }}
                            style={{
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.75rem 1.5rem',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            📄 {t('createInvoice')}
                        </button>
                    )}

                    {order.closed === 1 && (
                        <button
                            onClick={handleReopenOrder}
                            disabled={reopening}
                            style={{
                                backgroundColor: reopening ? '#ccc' : '#ff9800',
                                color: '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.75rem 1.5rem',
                                cursor: reopening ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {reopening ? t('reopening') + '...' : t('reopenOrder')}
                        </button>
                    )}

                    {canDeleteOrders && (
                        <button
                            onClick={handleDeleteOrder}
                            disabled={deleting}
                            style={{
                                backgroundColor: deleting ? '#ccc' : (theme?.colors?.error || '#d32f2f'),
                                color: '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                padding: '0.75rem 1.5rem',
                                cursor: deleting ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {deleting ? t('deleting') + '...' : '🗑️ ' + t('deleteOrder')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OrderDetailModal;